import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Button } from '../../ui'
import {
  ORGANISATION_PROFILES, isOrganisationProfile, setOrganisationProfile,
} from '../../lib/api'
import type { OrganisationProfile, WorkspaceSettings } from '../../lib/api'
import type { SettingsMutations } from './LiveSettings'
import { personOf, useSession } from './session'
import { MoreAbout, SettingsSection } from './SettingsSection'
import './live.css'

/** Each profile's words, its name and what it covers, by its stored value. */
const WORDS = Object.fromEntries(ORGANISATION_PROFILES.map((p) => [p.value, p])) as Record<
  OrganisationProfile,
  (typeof ORGANISATION_PROFILES)[number]
>

function profileOf(value: OrganisationProfile) {
  return WORDS[value]
}

/**
 * What kind of organisation this workspace is (migration 0175), and what that changes.
 *
 * CISA's baseline has a control only US federal civilian agencies must meet: DMARC aggregate reports
 * sent to CISA's own address. Defenssive raised it against every tenant and its own text told a
 * commercial customer to dismiss it. Now the workspace says what it is, and for anything but a US
 * federal civilian agency such a control is reported as not applicable, with the reason, instead of
 * as a finding. Every workspace starts as a commercial organisation, and the card says so.
 *
 * The controls it lists come from the server, which reads them from the checks' own declaration, so
 * the page cannot name a control the scan does not answer from the profile, or leave one out.
 *
 * An owner or admin can change it; the server decides that against the caller's session and audits
 * the change. The control is shown only to someone the server would let act (a presentation signal,
 * never the boundary). A profile the page cannot read is said to be unreadable, never guessed: it
 * decides whether a control is assessed at all.
 */
export function OrganisationProfileCard({
  w,
  mutations,
}: {
  w: WorkspaceSettings
  mutations: SettingsMutations
}) {
  const session = useSession()
  const myEmail = personOf(session)?.email?.toLowerCase() ?? null
  const canChange =
    myEmail !== null &&
    w.members.some(
      (m) =>
        m.email.toLowerCase() === myEmail &&
        (m.role === 'owner' || m.role === 'admin') &&
        m.status === 'active',
    )

  const current = isOrganisationProfile(w.organisationProfile) ? w.organisationProfile : null
  const controls = w.organisationProfileControls ?? null

  const selectId = useId()
  const [proposed, setProposed] = useState<OrganisationProfile | null>(current)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const statusRef = useRef<HTMLDivElement>(null)
  const busy = mutations.busy

  // Follow a profile that changed underneath us (another admin, or my own save landing) only while
  // the draft is untouched: an unsaved choice the viewer made is theirs and survives the refresh.
  const seen = useRef<OrganisationProfile | null>(current)
  useEffect(() => {
    if (current !== seen.current) {
      setProposed((draft) => (draft === null || draft === seen.current ? current : draft))
      seen.current = current
    }
  }, [current])

  function save(event: FormEvent) {
    event.preventDefault()
    if (proposed === null || proposed === current) return
    const chosen = profileOf(proposed).label
    void mutations.run(() => setOrganisationProfile(proposed), {
      onStart: () => {
        setError(null)
        setNotice(null)
        statusRef.current?.focus()
      },
      onResult: (result) => {
        if (result.ok) setNotice(`Saved: ${chosen}. The next assessment uses it.`)
        else setError(result.message)
        statusRef.current?.focus()
      },
    })
  }

  const federal = current === 'us_federal_civilian'

  return (
    <SettingsSection
      title="Organisation profile"
      description={
        <p>
          What kind of organisation this is, so Defenssive knows whether controls that CISA sets only
          for US federal civilian agencies apply to you. Every workspace starts as a commercial
          organisation until an owner or admin changes it here.
        </p>
      }
    >

      {current === null ? (
        <p className="live-error" role="alert">
          The organisation profile could not be read just now. Reload the page to try again.
        </p>
      ) : (
        <dl className="settings-grid">
          <dt>Profile</dt>
          <dd>
            {profileOf(current).label}
            <p className="settings-note">{profileOf(current).what}</p>
          </dd>

          <dt>What it changes</dt>
          <dd>
            {controls === null ? (
              <p className="settings-note" style={{ marginTop: 0 }}>
                The controls it decides could not be read just now.
              </p>
            ) : controls.length === 0 ? (
              <p className="settings-note" style={{ marginTop: 0 }}>
                No control Defenssive assesses depends on it at the moment.
              </p>
            ) : (
              <>
                {controls.length === 1
                  ? 'One control in CISA’s baseline applies only to US federal civilian agencies:'
                  : `${controls.length} controls in CISA’s baseline apply only to US federal civilian agencies:`}
                <ul className="settings-note" style={{ margin: 'var(--space-1) 0 0', paddingLeft: '1.2em' }}>
                  {controls.map((c) => (
                    <li key={c.key}>
                      {c.title}
                      {c.scubaReference ? <span className="mono text-muted"> ({c.scubaReference})</span> : null}
                    </li>
                  ))}
                </ul>
                {federal ? (
                  <p className="settings-note">
                    As a US federal civilian agency, you are assessed against them like any other control.
                  </p>
                ) : (
                  // What happens to such a finding, folded: the list above is what the profile
                  // decides; this is the detail of how an open one is closed.
                  <MoreAbout label="How this changes findings">
                    <p className="settings-note">
                      {'For this profile Defenssive does not raise them as findings: an assessment records them ' +
                        'as not applicable, with the reason, unless Exchange Online is not licensed for this ' +
                        'tenant or not connected. An open finding for one is closed by the next assessment that ' +
                        'can mark findings resolved; one that was set aside stays set aside. A closed one is ' +
                        'recorded as closed as not applicable, and is not counted as fixed.'}
                    </p>
                    <p className="settings-note">
                      Every other control, including DMARC report addresses of your own, applies to every
                      organisation and is assessed whatever the profile.
                    </p>
                  </MoreAbout>
                )}
              </>
            )}
            {federal || controls === null || controls.length === 0 ? (
              <p className="settings-note">
                Every other control, including DMARC report addresses of your own, applies to every
                organisation and is assessed whatever the profile.
              </p>
            ) : null}
          </dd>
        </dl>
      )}

      {current !== null && canChange ? (
        <form className="invite-form settings-inline-form" onSubmit={save} style={{ marginTop: 'var(--space-4)' }}>
          <label className="invite-field" htmlFor={selectId}>
            <span className="invite-label">Change the profile</span>
            <select
              id={selectId}
              className="findings-search-input"
              value={proposed ?? current}
              disabled={busy}
              onChange={(e) => {
                const next = e.target.value
                if (isOrganisationProfile(next)) setProposed(next)
              }}
            >
              {ORGANISATION_PROFILES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" variant="primary" disabled={busy || proposed === null || proposed === current}>
            Save profile
          </Button>
          {proposed !== null && proposed !== current ? (
            <p className="settings-note settings-inline-form-note">{profileOf(proposed).what}</p>
          ) : null}
        </form>
      ) : current !== null ? (
        <p className="settings-note">Only an owner or admin can change the organisation profile.</p>
      ) : null}

      <div ref={statusRef} tabIndex={-1} style={{ outline: 'none' }}>
        {error ? (
          <p className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
            {error}
          </p>
        ) : null}
        <p className="settings-note" aria-live="polite">{notice}</p>
      </div>
    </SettingsSection>
  )
}
